import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { formatDeviceName, isPrinterDevice } from '../lib/bluetoothPrinter';
import { Capacitor } from '@capacitor/core';
import { Search, Loader2, Bluetooth } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import {
  requestBluetoothDevice,
  connectBluetoothDevice,
  disconnectBluetoothDevice,
  isWebPlatform,
} from '@/lib/unifiedBluetoothPrinter';

interface PrintOptionsModalProps {
  open: boolean;
  onClose: () => void;
  onSelect: (options: { format: 'kohineer', size?: '2inch' | '3inch', deviceAddress?: string }) => void;
  onDownload: () => void;
  onShare?: () => void;
  devices?: Array<{ address: string; name: string }>;
  isScanning?: boolean;
  scanError?: string;
  onRetry?: () => void;
  defaultDeviceAddress?: string;
  isConnected?: boolean;
  connectedDeviceName?: string;
  // New prop to control print format selection visibility
  showPrintFormatSelection?: boolean;
  // New prop to control Bluetooth UI loading state
  isBluetoothUILoading?: boolean;
  // When true, hide Bluetooth selection and use default system print only (e.g. for mobile website)
  useSystemPrintOnly?: boolean;
  // When user selects a device from dropdown on native, auto-connect (callback for parent to connect). Can return Promise so modal can await.
  onConnectDevice?: (address: string) => void | Promise<void>;
  // When true, Print button shows "Printing..." and is disabled (prevents duplicate clicks on Android Bluetooth)
  isPrinting?: boolean;
}

export const PrintOptionsModal: React.FC<PrintOptionsModalProps> = ({
  open,
  onClose,
  onSelect,
  onDownload,
  onShare,
  devices,
  isScanning,
  scanError,
  onRetry,
  defaultDeviceAddress,
  isConnected,
  connectedDeviceName,
  showPrintFormatSelection = false, // Default to false for backward compatibility
  isBluetoothUILoading = false, // Default to false for backward compatibility
  useSystemPrintOnly = false, // Default to false - when true, hide Bluetooth and use system print (mobile web)
  onConnectDevice,
  isPrinting = false
}) => {
  const { toast } = useToast();
  const [size, setSize] = useState<'2inch' | '3inch'>('2inch');
  const [selectedDevice, setSelectedDevice] = useState<string>('');
  const [webBluetoothConnected, setWebBluetoothConnected] = useState(false);
  const [webBluetoothDeviceName, setWebBluetoothDeviceName] = useState<string>('');
  const [isConnectingWeb, setIsConnectingWeb] = useState(false);
  const [isConnectingNative, setIsConnectingNative] = useState(false);

  useEffect(() => {
    if (defaultDeviceAddress) {
      setSelectedDevice(defaultDeviceAddress);
    } else if (isConnected && devices && devices.length > 0) {
      // If connected but no default device, find the connected device in the list
      const connectedDevice = devices.find(device => device.address === defaultDeviceAddress);
      if (connectedDevice) {
        setSelectedDevice(connectedDevice.address);
      }
    }
  }, [defaultDeviceAddress, isConnected, devices]);

  // On native Android: when user selects a device from dropdown, connect (unless already connected to this device)
  useEffect(() => {
    if (!Capacitor.isNativePlatform() || !selectedDevice || !onConnectDevice) return;
    // Skip connect if already connected to this device (e.g. modal opened with last-used device) to avoid redundant reconnect
    if (isConnected && defaultDeviceAddress === selectedDevice) {
      setIsConnectingNative(false);
      return;
    }
    let cancelled = false;
    setIsConnectingNative(true);
    Promise.resolve(onConnectDevice(selectedDevice))
      .then(() => {
        if (!cancelled) setIsConnectingNative(false);
      })
      .catch(() => {
        if (!cancelled) setIsConnectingNative(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedDevice, isConnected, defaultDeviceAddress]);

  const handleSelectWebBluetooth = async () => {
    setIsConnectingWeb(true);
    try {
      // Check if site is HTTPS before attempting to connect
      if (typeof window !== 'undefined' && !window.isSecureContext) {
        toast({
          title: 'HTTPS Required',
          description: 'Web Bluetooth requires HTTPS. Please access this site using https:// instead of http://.',
          variant: 'destructive',
        });
        setIsConnectingWeb(false);
        return;
      }

      // Request user to select a device
      const device = await requestBluetoothDevice();
      
      if (!device) {
        toast({
          title: 'No Device Selected',
          description: 'Please select a Bluetooth printer to continue.',
          variant: 'destructive',
        });
        return;
      }

      // Connect to the selected device
      const connected = await connectBluetoothDevice(device.id);
      
      if (connected) {
        setWebBluetoothDeviceName(device.name || 'Unknown Printer');
        setWebBluetoothConnected(true);
        
        toast({
          title: 'Connected',
          description: `Successfully connected to ${device.name || 'printer'}`,
        });
      }
    } catch (error: any) {
      console.error('Error selecting/connecting to Web Bluetooth device:', error);
      
      // Provide more helpful error messages
      let errorDescription = error.message || 'Failed to connect to Bluetooth printer';
      
      if (error.message && error.message.includes('HTTPS')) {
        errorDescription = 'Web Bluetooth requires HTTPS. Please use https:// to access this site.';
      } else if (error.message && error.message.includes('permission')) {
        errorDescription = 'Bluetooth permission denied. Go to browser settings → Site Settings → Bluetooth → Allow, then try again.';
      }
      
      toast({
        title: 'Connection Failed',
        description: errorDescription,
        variant: 'destructive',
      });
    } finally {
      setIsConnectingWeb(false);
    }
  };

  const handleDisconnectWeb = async () => {
    try {
      await disconnectBluetoothDevice();
      setWebBluetoothDeviceName('');
      setWebBluetoothConnected(false);
      
      toast({
        title: 'Disconnected',
        description: 'Bluetooth printer disconnected',
      });
    } catch (error: any) {
      console.error('Error disconnecting from Web Bluetooth device:', error);
      toast({
        title: 'Disconnect Failed',
        description: error.message || 'Failed to disconnect from printer',
        variant: 'destructive',
      });
    }
  };

  const handleSelect = () => {
    onSelect({ format: 'kohineer', size, deviceAddress: selectedDevice });
    // onClose(); // Removed to let parent control modal closing
  };

  const showDeviceDropdown = devices && devices.length > 0;
  const isNativePlatform = Capacitor.isNativePlatform();
  const isWeb = isWebPlatform();
  
  // For web, always show print format selection if showPrintFormatSelection is true
  // For native platforms, show print format selection if showPrintFormatSelection is true
  const shouldShowPrintFormatSelection = showPrintFormatSelection;
  
  // For web, don't show loading state since we don't need to scan for Bluetooth devices. On native, also show loading when connecting to selected device.
  const shouldShowLoading = isNativePlatform && (isBluetoothUILoading || isConnectingNative);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md mx-auto w-[95vw] max-h-[90vh] overflow-y-auto">
        <DialogTitle className="text-lg font-semibold">Select Print Options</DialogTitle>
        <div className="flex flex-col gap-4 mt-2">
          {/* Print Format Selection - Show for both web and native platforms when enabled */}
          {shouldShowPrintFormatSelection && (
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <label className="font-semibold text-blue-800 mb-3 block">Print Format:</label>
              <div className="flex flex-col sm:flex-row gap-3">
                <label className="flex items-center gap-3 p-3 bg-white rounded border cursor-pointer hover:bg-blue-50 transition-colors">
                  <input 
                    type="radio" 
                    checked={size === '2inch'} 
                    onChange={() => setSize('2inch')}
                    className="w-4 h-4 text-blue-600"
                  /> 
                  <span className="font-medium">2mm (58mm)</span>
                </label>
                <label className="flex items-center gap-3 p-3 bg-white rounded border cursor-pointer hover:bg-blue-50 transition-colors">
                  <input 
                    type="radio" 
                    checked={size === '3inch'} 
                    onChange={() => setSize('3inch')}
                    className="w-4 h-4 text-blue-600"
                  /> 
                  <span className="font-medium">3mm (80mm)</span>
                </label>
              </div>
            </div>
          )}
          
          {/* Web Bluetooth UI - hidden when useSystemPrintOnly (e.g. mobile website uses default system print) */}
          {isWeb && !useSystemPrintOnly && (
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <label className="font-semibold text-blue-800 mb-3 block flex items-center gap-2">
                <Bluetooth className="h-4 w-4" />
                Web Bluetooth Printer:
              </label>
              
              {webBluetoothConnected ? (
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-green-600 text-sm bg-green-50 p-3 rounded border border-green-200">
                    <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                    <span>✓ Connected to: <strong>{webBluetoothDeviceName}</strong></span>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleDisconnectWeb}
                    className="w-full"
                  >
                    Disconnect
                  </Button>
                </div>
              ) : (
                <div className="space-y-2">
                  <Button
                    onClick={handleSelectWebBluetooth}
                    disabled={isConnectingWeb}
                    variant="outline"
                    className="w-full"
                  >
                    {isConnectingWeb ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Connecting...
                      </>
                    ) : (
                      <>
                        <Bluetooth className="mr-2 h-4 w-4" />
                        Select Bluetooth Printer
                      </>
                    )}
                  </Button>
                  <div className="text-xs text-gray-600">
                    <p className="font-medium">Browser Support:</p>
                    <p>Chrome, Edge, and Opera on desktop & Android</p>
                  </div>
                </div>
              )}
            </div>
          )}
          
          {/* Only show Android Bluetooth UI on native platforms */}
          {isNativePlatform && (
            <>
              {isScanning && (
                <div className="flex items-center gap-2 text-blue-600 text-sm">
                  <span className="loader inline-block w-4 h-4 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
                  Scanning for Bluetooth printers...
                </div>
              )}
              {scanError && (
                <div className="text-red-600 text-sm flex flex-col gap-2">
                  <span>{scanError}</span>
                  {onRetry && <Button onClick={onRetry} variant="outline">Retry</Button>}
                </div>
              )}
              {isConnected && connectedDeviceName && (
                <div className="flex items-center gap-2 text-green-600 text-sm bg-green-50 p-3 rounded border">
                  <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                  <span>✓ Connected to: <strong>{connectedDeviceName}</strong></span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      // Reset connection state to show device selection
                      setSelectedDevice('');
                      if (onRetry) {
                        onRetry(); // This will trigger a new scan and disconnect
                      }
                    }}
                    className="ml-auto text-xs px-2 py-1"
                    disabled={isBluetoothUILoading}
                  >
                    {isBluetoothUILoading ? 'Disconnecting...' : 'Disconnect'}
                  </Button>
                </div>
              )}
              {showDeviceDropdown && !isScanning && !scanError && !(isConnected && connectedDeviceName) && (
                <div>
                  {isConnectingNative && selectedDevice && (
                    <div className="flex items-center gap-2 text-blue-600 text-sm mb-2">
                      <span className="loader inline-block w-4 h-4 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
                      Connecting to printer...
                    </div>
                  )}
                  <div className="flex items-center justify-between mb-2">
                    <label className="font-semibold">
                      Bluetooth Printer:
                    </label>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={onRetry}
                      className="text-xs px-2 py-1"
                    >
                      <Search className="h-3 w-3 mr-1" />
                      Scan More
                    </Button>
                  </div>
                  <select
                    className="w-full border rounded px-2 py-1 mt-1"
                    value={selectedDevice}
                    onChange={e => setSelectedDevice(e.target.value)}
                  >
                    <option value="">Select Printer</option>
                    {devices.map(device => (
                      <option 
                        key={device.address} 
                        value={device.address}
                        style={{ fontWeight: isPrinterDevice(device) ? 'bold' : 'normal' }}
                      >
                        {formatDeviceName(device)}
                        {isPrinterDevice(device) && ' 🖨️'}
                      </option>
                    ))}
                  </select>
                  {devices.length > 0 && (
                    <div className="text-xs text-gray-600 mt-1">
                      Found {devices.length} device(s). Printer devices are marked with 🖨️
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
        <div className="flex flex-col sm:flex-row gap-2 mt-4">
          <Button onClick={onClose} variant="secondary" className="flex-1 sm:flex-none">Cancel</Button>
          <Button onClick={onDownload} variant="outline" className="flex-1 sm:flex-none">Download</Button>
          <Button 
            onClick={handleSelect} 
            variant="default" 
            disabled={
              isPrinting ||
              shouldShowLoading || 
              (isNativePlatform && showDeviceDropdown && !selectedDevice && !(isConnected && connectedDeviceName)) ||
              (isWeb && !useSystemPrintOnly && !webBluetoothConnected)
            }
            className="flex-1 sm:flex-none bg-green-600 hover:bg-green-700"
          >
            {isPrinting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Printing...
              </>
            ) : shouldShowLoading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Loading...
              </>
            ) : (
              'Print'
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Add a simple CSS loader for the spinner
// .loader { border-width: 2px; border-style: solid; border-radius: 9999px; border-top-color: transparent; animation: spin 1s linear infinite; }
// @keyframes spin { to { transform: rotate(360deg); } } 